// Pure helpers for the AI Assistant screens: no React, no API, so they are easy to test.

export const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" }
];

// What the assistant does when no hours are saved: the same default the server applies.
export const DEFAULT_HOURS = {
  mon: ["09:00", "18:00"],
  tue: ["09:00", "18:00"],
  wed: ["09:00", "18:00"],
  thu: ["09:00", "18:00"],
  fri: ["09:00", "18:00"],
  sat: ["09:00", "18:00"]
};

// API hours ({mon: ["09:00","18:00"], ...}, a missing day = closed, null = default) -> the form's rows.
export function hoursToForm(hours) {
  const source = hours || DEFAULT_HOURS;
  const form = {};
  DAYS.forEach(({ key }) => {
    const pair = source[key];
    form[key] = Array.isArray(pair) && pair.length === 2 ? { open: true, from: pair[0], to: pair[1] } : { open: false, from: "09:00", to: "18:00" };
  });
  return form;
}

// The form's rows -> API hours. Closed days are left out, which is how the server reads "closed".
export function formToHours(form) {
  const out = {};
  DAYS.forEach(({ key }) => {
    const row = form[key];
    if (row && row.open) out[key] = [row.from, row.to];
  });
  return out;
}

// -> [] when fine, else messages to show next to the form.
export function validateHoursForm(form) {
  const errors = [];
  DAYS.forEach(({ key, label }) => {
    const row = form[key];
    if (!row || !row.open) return;
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(row.from || "") || !/^([01]\d|2[0-3]):[0-5]\d$/.test(row.to || "")) {
      errors.push(`${label}: enter the opening and closing time`);
    } else if (row.to <= row.from) {
      errors.push(`${label}: closing time must be after opening time`);
    }
  });
  if (DAYS.every(({ key }) => !form[key] || !form[key].open)) errors.push("Open at least one day, or the assistant will always say you are closed");
  return errors;
}

export const MODES = [
  { value: "off", label: "Off", help: "The assistant does nothing. Customer messages wait in your inbox, as they always did." },
  {
    value: "shadow",
    label: "Shadow (drafts only)",
    help: "The assistant writes a suggested reply under each customer message. Nothing is sent to the customer: your team sends, edits or discards it."
  },
  {
    value: "live",
    label: "Live",
    help: "The assistant replies to customers on WhatsApp by itself, until a team member takes the chat or it hands the chat over."
  }
];

export const modeLabel = (mode) => (MODES.find((m) => m.value === mode) || MODES[0]).label;

const LANGUAGE_LABELS = { en: "English", ta: "Tamil", ml: "Malayalam", te: "Telugu", kn: "Kannada", other: "Other language" };
export const languageLabel = (code) => LANGUAGE_LABELS[code] || (code ? String(code).toUpperCase() : "");

const HANDOFF_LABELS = {
  customer_request: "The customer wants a person, or to book",
  complaint: "Complaint",
  refund_or_damage: "Refund or damage",
  upset: "The customer is upset",
  not_understood: "The assistant did not understand",
  out_of_scope: "Outside what the assistant can help with",
  tool_error: "The assistant hit a problem",
  assigned: "The chat was assigned to a team member",
  staff_replied: "A team member replied",
  staff_took_over: "A team member took over"
};
export const handoffLabel = (reason) => HANDOFF_LABELS[reason] || (reason ? String(reason).replace(/_/g, " ") : "");

// How the shadow drafts fared: what share staff sent as written or with edits rather than discarding.
export function draftAcceptance(drafts) {
  const d = drafts || {};
  const sent = Number(d.sent || 0);
  const edited = Number(d.edited || 0);
  const discarded = Number(d.discarded || 0);
  const total = sent + edited + discarded;
  if (!total) return { total: 0, asWritten: null, used: null };
  return { total, asWritten: Math.round((sent / total) * 100), used: Math.round(((sent + edited) / total) * 100) };
}

export function formatTokens(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "2026-10" -> "Oct 2026"
export function monthLabel(ym) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || ""));
  return m ? `${MONTHS[Number(m[2]) - 1] || m[2]} ${m[1]}` : String(ym || "");
}

// The most useful time zones first, then whatever the account already has.
export function timeZoneChoices(current) {
  const base = ["Asia/Kolkata", "Asia/Dubai", "Asia/Riyadh", "Asia/Muscat", "Asia/Qatar", "Asia/Kuwait", "Asia/Bahrain", "Asia/Singapore", "Europe/London"];
  return current && !base.includes(current) ? [current, ...base] : base;
}

// An article is usable by the assistant only with an answer.
export const articleStatus = (a) => (!a.body ? "needs-answer" : a.active ? "on" : "off");

// Whether the settings form differs from what was loaded, so Save can be disabled when there is nothing to save.
export function settingsChanged(saved, form) {
  const norm = (s) =>
    JSON.stringify({
      mode: s.mode,
      languages: [...(s.languages || [])].sort(),
      business_name: s.business_name || "",
      greeting: s.greeting || "",
      tone_notes: s.tone_notes || "",
      timezone: s.timezone,
      hours: s.hours,
      holidays: [...(s.holidays || [])].sort()
    });
  return norm(saved) !== norm(form);
}
